<script lang="ts">
  import type { CalendarMonth, CalendarDay } from "../lib/swimCalendar";
  import { formatMonthSummary } from "../lib/swimCalendar";

  interface Props {
    month: CalendarMonth;
    onSelectDay: (day: CalendarDay) => void;
    selectedDayKey?: string | null;
    /** "dots" = the Swim tab's swims-per-day dots; "count" = the Club calendar's
     *  number of swimmers that day (clubCalendar.ts fills day.swimmers). */
    marker?: "dots" | "count";
    summary?: (month: CalendarMonth) => string;
  }
  let { month, onSelectDay, selectedDayKey = null, marker = "dots", summary = formatMonthSummary }: Props = $props();

  const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

  const dayKey = (d: CalendarDay) =>
    `${d.date.getFullYear()}-${d.date.getMonth()}-${d.date.getDate()}`;

  function tappable(day: CalendarDay): boolean {
    return day.swims.length > 0 && !day.isFuture;
  }

  function ariaForDay(day: CalendarDay): string {
    const dateLabel = `${month.label.split(" ")[0]} ${day.dayOfMonth}`;
    if (day.isFuture) return `${dateLabel}, not available`;
    if (day.swims.length === 0) return dateLabel;
    if (marker === "count") {
      const n = day.swimmers ?? 0;
      return `${dateLabel}, ${n} ${n === 1 ? "swimmer" : "swimmers"}`;
    }
    if (day.swims.length === 1) {
      const s = day.swims[0];
      const kind = s.isOpenWater ? "open water swim" : "swim";
      return s.distanceMeters == null
        ? `${dateLabel}, ${kind}`
        : `${dateLabel}, ${kind}, ${Math.round(s.distanceMeters).toLocaleString()} metres`;
    }
    const owSuffix = day.swims.some((s) => s.isOpenWater) ? ", including open water" : "";
    return `${dateLabel}, ${day.swims.length} swims${owSuffix}`;
  }
</script>

<section class="month" data-month aria-label={month.label}>
  <div class="month-sticky">
    <header class="month-head">
      <h3 class="month-label">{month.label}</h3>
      <p class="month-summary">{summary(month)}</p>
    </header>

    <div class="weekday-row" aria-hidden="true">
      {#each WEEKDAYS as wd, i (i)}
        <span class="weekday">{wd}</span>
      {/each}
    </div>
  </div>

  <div class="grid">
    {#each Array(month.leadingBlanks) as _, i (`blank-${i}`)}
      <span class="cell blank" aria-hidden="true"></span>
    {/each}

    {#each month.days as day (day.dayOfMonth)}
      {#if tappable(day)}
        <button
          type="button"
          class="cell day swim"
          class:today={day.isToday}
          class:selected={dayKey(day) === selectedDayKey}
          data-testid="calendar-day"
          data-day-key={dayKey(day)}
          aria-label={ariaForDay(day)}
          onclick={() => onSelectDay(day)}
        >
          <span class="num">{day.dayOfMonth}</span>
          {#if marker === "count"}
            <span
              class="count"
              class:count-many={(day.swimmers ?? 0) >= 2}
              data-testid="calendar-count"
              aria-hidden="true"
            >{day.swimmers ?? 0}</span>
          {:else}
            <span class="dots" aria-hidden="true">
              {#each day.swims.slice(0, 2) as swim, i (i)}
                <span class="dot" class:dot-ow={swim.isOpenWater}></span>
              {/each}
            </span>
          {/if}
        </button>
      {:else}
        <span
          class="cell day"
          class:today={day.isToday}
          class:future={day.isFuture}
          class:rest={!day.isFuture && day.swims.length === 0}
          aria-label={ariaForDay(day)}
          aria-disabled={day.isFuture ? "true" : undefined}
        >
          <span class="num">{day.dayOfMonth}</span>
        </span>
      {/if}
    {/each}
  </div>
</section>

<style>
  .month {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .month-sticky {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--bg-canvas);
    padding-top: var(--space-2);
  }
  .month-head {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0 var(--space-1);
  }
  .month-label {
    margin: 0;
    font-family: var(--font-display);
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
  }
  .month-summary {
    margin: 0;
    font-size: 15px;
    color: var(--text-muted);
  }
  .weekday-row,
  .grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }
  .weekday-row {
    margin-top: var(--space-1);
  }
  .weekday {
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-dim);
    padding-bottom: 4px;
  }
  .grid {
    row-gap: 2px;
  }
  .cell {
    height: 58px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 0;
    background: transparent;
    font-family: var(--font-text);
  }
  .cell.blank {
    height: 58px;
  }
  button.cell.day {
    cursor: pointer;
    border-radius: var(--radius-button);
  }
  button.cell.day:active {
    background: rgba(183, 199, 232, 0.10);
  }
  .num {
    font-size: 18px;
    font-weight: 500;
    line-height: 1;
    color: var(--text-primary);
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cell.rest .num {
    color: var(--text-muted);
    font-weight: 400;
  }
  .cell.future .num {
    color: var(--text-dim);
    font-weight: 400;
  }
  .cell.today .num {
    border: 1.5px solid rgba(255, 255, 255, 0.55);
    border-radius: var(--radius-pill);
  }
  .cell.day.selected .num {
    box-shadow: 0 0 0 2px var(--accent-primary);
    border-radius: var(--radius-pill);
  }
  .dots {
    display: flex;
    gap: 4px;
    height: 8px;
    align-items: center;
  }
  .dot {
    width: 7px;
    height: 7px;
    border-radius: var(--radius-pill);
    background: var(--accent-primary);
  }
  /* Open-water swim marker: the water-role accent (sky blue) instead of the
     default pool dot, so an open-water day reads distinctly at a glance. */
  .dot-ow {
    background: var(--accent-secondary);
  }
  /* Club calendar marker: the number of swimmers that day, in the dots' slot.
     One swimmer is quiet; two or more is what the Club calendar is for, so it
     reads bold in the accent. Two digits fit a 390 pt column (about 51 px). */
  .count {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 16px;
    font-size: 14px;
    line-height: 1;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: var(--text-muted);
  }
  .count-many {
    font-weight: 800;
    color: var(--accent-primary);
  }
</style>
